import s from './teacher-packs-card.module.scss'
import classNames from 'classnames'
import useMedia from 'use-media'
import { IconArrowRight, IconCheck, IconMusic, IconClock, IconCalendar, IconTag } from '@tabler/icons-react'
import { getNoun } from '@utils/lib/getNoun'

const PACK_DESCRIPTIONS_DESKTOP = {
    5: 'Попробовать регулярные занятия и понять свой темп',
    10: 'Оптимальный вариант для стабильного прогресса',
    15: 'Для тех, кто хочет быстрее прогрессировать',
}

const PACK_DESCRIPTIONS_MOBILE = {
    5: 'Попробовать и найти свой темп',
    10: 'Оптимальный вариант',
    15: 'Для стабильного результата',
}

export const TeacherPacksCard = ({ packs = [], singleLessonPrice, onSelect, loadingId, selectedId, showEmpty, selectable, disabled, disabledMessage, variant = 'list' }) => {
    const isMobile = useMedia({ maxWidth: '768px' })
    const packDescriptions = isMobile ? PACK_DESCRIPTIONS_MOBILE : PACK_DESCRIPTIONS_DESKTOP
    const isCards = variant === 'cards'
    const isEmpty = !packs || packs.length === 0

    if (isEmpty && !showEmpty) return null

    if (isEmpty) {
        return (
            <div className={s.card}>
                {/* <Illustration/> */}
                <div className={s.content}>
                    <div className={s.header}>
                        <h5 className={s.title}>У преподавателя еще нет пакетов</h5>
                        <p className={s.subtitle}>Мы уже оповестили преподавателя о возможности предлагать пакеты занятий</p>
                    </div>
                </div>
            </div>
        )
    }

    const getEconomy = (pack) =>
        (singleLessonPrice && pack?.lessons_count ? singleLessonPrice * pack.lessons_count - pack.price : null)

    if (isCards) {
        return (
            <div className={s.cardsGrid}>
                {packs.map((pack) => {
                    const isSelected = selectedId === pack?.id
                    const oldPrice = singleLessonPrice && pack?.lessons_count
                        ? singleLessonPrice * pack.lessons_count
                        : null
                    const discountPercent = oldPrice && pack?.price
                        ? Math.max(0, Math.round((1 - pack.price / oldPrice) * 100))
                        : null
                    const lessonDuration = pack?.duration || 50
                    const validityDays = pack?.validity_days || 30

                    return (
                        <div
                            key={pack?.id}
                            className={classNames(s.cardsItem, isSelected && s.cardsItemSelected)}
                            onClick={() => !disabled && onSelect?.(pack)}
                            role='button'
                            tabIndex={disabled ? -1 : 0}
                            aria-pressed={isSelected}
                        >
                            <div className={s.cardsTop}>
                                <p className={s.cardsDesc}>
                                    {packDescriptions[pack?.lessons_count] || 'Выгодный пакет занятий'}
                                </p>
                                <div className={s.cardsTitleRow}>
                                    <IconMusic size={20} className={s.cardsMusicIcon} />
                                    <span className={s.cardsTitle}>
                                        {pack?.lessons_count} {getNoun(pack?.lessons_count, 'занятие', 'занятия', 'занятий')}
                                    </span>
                                </div>
                                <div className={s.cardsBadges}>
                                    <span className={s.cardsBadge}>
                                        <IconClock size={14} className={s.cardsBadgeIcon} />
                                        по {lessonDuration} минут
                                    </span>
                                    <span className={s.cardsBadge}>
                                        <IconCalendar size={14} className={s.cardsBadgeIcon} />
                                        Действует {validityDays} дней
                                    </span>
                                    {discountPercent > 0 && (
                                        <span className={classNames(s.cardsBadge, s.cardsBadgeDiscount, s.cardsBadgeDiscountMobile)}>
                                            −{discountPercent}%
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className={s.cardsBottom}>
                                <div className={s.cardsOldPriceRow}>
                                    {oldPrice > 0 && (
                                        <span className={s.cardsOldPrice}>
                                            {oldPrice.toLocaleString('ru-RU')} ₽
                                        </span>
                                    )}
                                    {discountPercent > 0 && (
                                        <span className={classNames(s.cardsBadge, s.cardsBadgeDiscount, s.cardsBadgeDiscountDesktop)}>
                                            −{discountPercent}%
                                        </span>
                                    )}
                                </div>
                                <div className={s.cardsPriceRow}>
                                    <IconTag size={20} className={s.cardsPriceIcon} />
                                    <span className={s.cardsPrice}>
                                        {pack?.price?.toLocaleString('ru-RU')} ₽
                                    </span>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    return (
        <div className={classNames(s.card, disabled && s.cardDisabled)}>
            {/* <Illustration/> */}
            <div className={s.content}>
                <div className={s.header}>
                    <h5 className={s.title}>{disabled ? 'Покупка пакета недоступна' : 'У преподавателя есть пакеты'}</h5>
                    {!disabled && <p className={s.subtitle}>Ознакомьтесь с выгодными пакетами, распланируйте занятия и сэкономьте на стоимости занятиях.</p>}
                    {disabled && disabledMessage && (
                        <div className={s.notice}>{disabledMessage}</div>
                    )}
                </div>

                <div className={classNames(s.list, disabled && s.listDisabled)}>
                    {!disabled && packs.map((pack) => {
                        const economy = getEconomy(pack)
                        const isSelected = selectedId === pack?.id
                        return (
                            <div className={s.packRow} key={pack?.id}>
                                <span className={s.lessons}>{pack?.lessons_count} занятий</span>
                                <div className={s.right}>
                                    {economy > 0 ? <span className={s.badge}>-{economy.toLocaleString('ru-RU')} руб.</span> : <></>}
                                    <span className={s.price}>{pack?.price?.toLocaleString('ru-RU')} р.</span>
                                    {selectable && !disabled ? (
                                        <button
                                            type='button'
                                            className={classNames(s.checkbox, isSelected && s.checkboxSelected)}
                                            onClick={() => !disabled && onSelect?.(pack)}
                                            disabled={loadingId === pack?.id || disabled}
                                            aria-label='Выбрать пакет'
                                        >
                                            {isSelected ? <IconCheck size={24}/> : null}
                                        </button>
                                    ) : (
                                        <button
                                            type='button'
                                            className={s.arrow}
                                            onClick={() => !disabled && onSelect?.(pack)}
                                            disabled={loadingId === pack?.id || disabled}
                                            aria-label='Выбрать пакет'
                                        >
                                            <IconArrowRight size={24}/>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
